import {
  Box,
  Button,
  Input as ChakraInput,
  HStack,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  Stack,
  Text,
  Tooltip,
  useToast as useChakraToast,
  useRadioGroup,
  useStyleConfig,
  VStack,
} from "@chakra-ui/react";
import {
  forwardRef,
  memo,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Form, Formik, FormikContext, useField } from "formik";
import "./index.css";
import Select from "react-select";
import { get, set, del } from "idb-keyval";
import { FiSearch } from "react-icons/fi";
import { MdAutoAwesome } from "react-icons/md";
import { PiCheckCircleThin } from "react-icons/pi";
import { HiOutlineExclamationCircle, HiXMark } from "react-icons/hi2";
import { useFirebase } from "../wrappers/firebase";
import React from "react";

/**
 * @typedef {import("@chakra-ui/react").InputProps & {
 *   title?: string,
 *   titleAddon?: React.ReactNode,
 *   name?: string,
 *   error?: string,
 *   container?: import("@chakra-ui/react").StackProps,
 *   input: import("@chakra-ui/react").InputProps
 * }} CustomInputProps
 */

/**
 * @type {React.ForwardRefExoticComponent<
 *   CustomInputProps & React.RefAttributes<HTMLInputElement>
 * >}
 */
const Input = forwardRef(
  (
    { title, titleAddon, name, container, error: explicitError, ...props },
    ref
  ) => {
    let field = {};
    let meta = {};
    let error;

    const formikContext = useContext(FormikContext);

    if (name && formikContext) {
      [field, meta] = useField(name);
      // Prefer explicit error, else Formik meta error if touched
      error =
        explicitError ?? (meta.touched && meta.error ? meta.error : undefined);
    } else {
      // Outside Formik, use explicit error prop
      error = explicitError;
      // Manually bind handlers if passed in props
      field = {
        name,
        onChange: props.onChange,
        onBlur: props.onBlur,
        defaultValue: props.value ?? "",
      };
    }

    return (
      <VStack
        w={props.w || props.width}
        h={props.h || props.height}
        align="flex-start"
        spacing="1"
        {...container}
      >
        {(title || titleAddon) && (
          <HStack
            justify="space-between"
            w="full"
            fontSize="xs"
            color={error ? "red.400" : undefined}
          >
            {title && <Text>{error || title}</Text>}
            {titleAddon}
          </HStack>
        )}
        <ChakraInput
          ref={ref}
          {...field}
          {...props}
          {...props.input}
          isInvalid={!!error}
        />
      </VStack>
    );
  }
);

/**
 * Editable text component.
 *
 * @typedef {Object} TextEditableProps
 * @property {string} value - The current text value to display in the editable field.
 * @property {(value: string) => void} onChange - Callback fired when the text is confirmed (e.g., on Enter key).
 * @property {boolean} autoFocus
 * @property {string} placeholder
 * @property {number} maxCharCount
 *
 * @param {TextEditableProps & import("@chakra-ui/react").TextProps} props
 * @returns {JSX.Element}
 */
const TextEditable = (props) => {
  const { value, onChange, maxCharCount, ...rest } = props;
  const textRef = useRef(null);
  const [isCharWarningOpen, setIsCharWarningOpen] = useState(false);

  useEffect(() => {
    if (props.autoFocus) textRef.current?.focus();
  }, [props.autoFocus]);

  const listenToEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent newline insertion
      if (textRef.current) {
        textRef.current.blur();
        textRef.current.innerHTML = value;
      }
    }
  };

  const handleInput = (e) => {
    const text = e.target.textContent;

    if (maxCharCount && text.length > maxCharCount) {
      // Trim the extra characters
      setIsCharWarningOpen(true);
      e.target.textContent = text.slice(0, maxCharCount);

      // Move cursor to the end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(e.target);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      setIsCharWarningOpen(false);
    }
  };

  const confirmChange = () => {
    if (textRef.current && props?.onChange) {
      props.onChange(textRef.current.textContent);
    }
  };

  return (
    <_Tooltip
      placement="bottom"
      isOpen={isCharWarningOpen}
      label={`Maximum ${props.maxCharCount} characters`}
    >
      <Text
        ref={textRef}
        contentEditable
        border="none"
        borderBottom="1px solid transparent"
        _hover={{
          borderBottomColor: "gray.600",
        }}
        _focus={{
          borderBottomColor: "gray.300",
          textOverflow: "unset",
          whiteSpace: "unset",
          noOfLines: "auto",
          color: "white",
        }}
        onKeyDown={listenToEnter}
        onInput={handleInput}
        suppressContentEditableWarning
        outline={"none !important"}
        textOverflow={"ellipsis"}
        whiteSpace="nowrap"
        wordBreak="break-all"
        onBlur={confirmChange}
        spellCheck={false}
        {...rest}
      >
        {props.value || props.placeholder}
      </Text>
    </_Tooltip>
  );
};

/**
 *
 * @param {import("@chakra-ui/react").BoxProps & {primaryColor?: string}} props
 * @returns
 */
const Separator = (props) => (
  <Box
    {...props}
    bgGradient={`radial(${
      props.primaryColor || "#3e3e46"
    } 45%, transparent, transparent)`}
  />
);

/**
 *
 * @param {import("@chakra-ui/react").BoxProps & {isLoaded?: boolean, startColor?: string, endColor?: string, direction?: string}} props
 * @returns
 */
const Skeleton = (props) => {
  const {
    isLoaded,
    startColor = "#212123",
    endColor = "#161618",
    direction = "to right",
    children,
    ...rest
  } = props;

  const skull_container = useRef();
  useEffect(() => {
    if (props.isLoaded) {
      skull_container.current.style.background = "transparent";
    }
  }, [props.isLoaded]);
  return (
    <Box
      ref={skull_container}
      className={!isLoaded ? "skeleton-v2" : ""}
      {...rest}
      style={{
        "--start-color": props.startColor || "#212123",
        "--end-color": props.endColor || "#161618",
        "--direction": props.direction || "to right",
      }}
    >
      <Box
        w="full"
        h="full"
        // transition={"all 150ms"}
        opacity={props.isLoaded ? "1" : 0}
      >
        {props.children}
      </Box>
    </Box>
  );
};

/**
 *
 * @param {import("react-select").Props} props
 * @returns
 */
const _Select = (props) => {
  return (
    <Select
      {...props}
      styles={{
        container: (baseStyles, state) => ({
          ...baseStyles,
          width: "100%",
          padding: "0px 2px",
        }),
        dropdownIndicator: (baseStyles) => ({
          ...baseStyles,
          color: "#3e3e46",
        }),
        indicatorSeparator: (baseStyles) => ({
          ...baseStyles,
          background: "#3e3e46",
        }),
        control: (baseStyles, state) => ({
          ...baseStyles,
          background: "#1a1a1d",
          color: "white",
          borderColor: "#303036",
          ":hover": {
            borderColor: "#3e3e46",
          },
          fontSize: "14px",
        }),
        singleValue: (baseStyles) => ({
          ...baseStyles,
          color: "white",
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
          color: "white",
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          color: "white",
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          background: "#313134",
          padding: "0px",
          width: "100%",
          left: "2px",
          right: "2px",
          height: "max-content",
          borderRadius: "5px",
          maxHeight: "300px",
          overflowY: "scroll",
          border: "1px solid #3e3e46",
        }),
        menuList: (baseStyles) => ({
          ...baseStyles,
          padding: 0,
          borderRadius: "5px",
        }),
        option: (baseStyles) => ({
          ...baseStyles,
          color: "white",
          fontSize: "14px",
          fontWeight: "600",
          background: "#313134",
          height: "max-content",
          ":active": {
            background: "#241933",
          },
          ":hover": {
            background: "#2a2a2c",
          },
        }),
        multiValueLabel: (baseStyles) => ({
          ...baseStyles,
          color: "white",
          background: "#2a2a2c",
        }),
        multiValueRemove: (baseStyles) => ({
          ...baseStyles,
          background: "#2a2a2c",
        }),
        multiValue: (baseStyles) => ({
          ...baseStyles,
          background: "#241933",
        }),
      }}
    />
  );
};

/**
 * @typedef {import("@chakra-ui/react").BoxProps & {
 *   src?: string,
 *   fallbackSrc?: string,
 *   onLoad?: (img: HTMLImageElement) => void,
 *   firebaseStoragePath?: string
 * }} ImageProps
 */

/** @type {import("react").FC<ImageProps>} */
const _Image = memo(
  forwardRef((props, ref) => {
    const {
      src,
      fallbackSrc = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=",
      width,
      height,
      onLoad,
      firebaseStoragePath,
      ...rest
    } = props;

    const CACHE_TTL = 1000 * 60 * 60 * 24 * 7; // 7 days
    const [finalSrc, setFinalSrc] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const { fetchSecureFile } = useFirebase();

    useEffect(() => {
      setFinalSrc(null);
      setIsLoaded(false);
      setHasError(false);

      const hasUndefined =
        src?.includes("undefined") ||
        firebaseStoragePath?.includes("undefined");
      if ((!src && !firebaseStoragePath) || hasUndefined) return;

      let revokeURL = null;
      const cacheKey = src || firebaseStoragePath;
      const metaKey = `${cacheKey}_meta`;
      const fallbackKey = `${cacheKey}_fallback_url`;

      const isExpired = (timestamp) => Date.now() - timestamp > CACHE_TTL;

      const loadImage = async () => {
        try {
          console.debug("[_Image] init", {
            cacheKey,
            src,
            firebaseStoragePath,
          });

          // 1) Try cache first using a stable key (src or firebaseStoragePath)
          const [cachedBlob, meta] = await Promise.all([
            get(cacheKey),
            get(metaKey),
          ]);
          if (cachedBlob && meta && !isExpired(meta.timestamp)) {
            console.debug("[_Image] cache hit", { cacheKey, meta });
            const url = URL.createObjectURL(cachedBlob);
            setFinalSrc(url);
            revokeURL = url;
            return;
          }

          console.debug("[_Image] cache miss or expired", { cacheKey, meta });

          // 2) Fetch fresh
          let fetchedBlob = null;
          if (firebaseStoragePath) {
            console.debug("[_Image] fetching from firebase storage", {
              firebaseStoragePath,
            });
            try {
              const file = await fetchSecureFile({
                path: `${firebaseStoragePath}?alt=media`,
              });
              fetchedBlob = await file.blob();
            } catch (err) {
              console.warn("[_Image] firebase fetch failed", err);
              if (!src) throw err;
            }
          }

          // If not firebase path or firebase failed but we have src, try network fetch via src
          if (!fetchedBlob && src) {
            console.debug("[_Image] fetching from src", { src });
            const res = await fetch(src, { mode: "cors" });
            if (!res.ok || res.type === "opaque")
              throw new Error("Not CORS-safe");
            fetchedBlob = await res.blob();
          }

          if (!fetchedBlob) {
            throw new Error("No image blob available after fetch attempts");
          }

          const objectUrl = URL.createObjectURL(fetchedBlob);
          setFinalSrc(objectUrl);
          revokeURL = objectUrl;
          await set(cacheKey, fetchedBlob);
          await set(metaKey, { timestamp: Date.now() });
          console.debug("[_Image] cache set", { cacheKey });
        } catch (err) {
          console.warn(
            "[_Image] fetch failed. Using fallback and caching fallback url",
            err
          );

          const fallback = {
            url: src || firebaseStoragePath,
            timestamp: Date.now(),
          };
          await set(fallbackKey, fallback);
          setFinalSrc(src || firebaseStoragePath); // fallback will still work visually
        }
      };

      loadImage();

      return () => {
        if (revokeURL) {
          URL.revokeObjectURL(revokeURL);
          console.debug("[_Image] revoked object URL");
        }
      };
    }, [src, firebaseStoragePath]);

    useEffect(() => {
      if (!finalSrc) return;

      const testImg = new Image();
      testImg.src = finalSrc;
      testImg.onload = () => {
        setIsLoaded(true);
        setHasError(false);
        onLoad?.(testImg);
      };
      testImg.onerror = () => {
        setIsLoaded(true);
        setHasError(true);
        console.error("[_Image] image load error", { finalSrc });
      };
    }, [finalSrc]);

    const imageSrc = hasError ? fallbackSrc : finalSrc;

    return (
      <Skeleton
        isLoaded={isLoaded}
        overflow="hidden"
        w={width || props.w || "full"}
        h={height || props.h || "full"}
        borderRadius={props.borderRadius}
      >
        <Box
          as="img"
          w={width || props.w || "full"}
          h={height || props.h || "full"}
          src={imageSrc}
          objectFit={"cover"}
          ref={ref}
          {...rest}
        />
      </Skeleton>
    );
  })
);

const RadioCardGroup = ({
  name,
  value: controlledValue,
  defaultValue,
  onChange,
  children,
  direction = "row",
  spacing = 2,
  allowChange = true,
  ...rest
}) => {
  const {
    getRootProps,
    value,
    onChange: internalOnChange,
  } = useRadioGroup({
    name,
    defaultValue,
    value: controlledValue,
    onChange,
  });

  const groupValue = value;
  const rootProps = getRootProps();

  // Clone children so we can inject props into each RadioCard
  const enhancedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, {
      isSelected: child.props.value === groupValue,
      onSelectChange: internalOnChange,
      allowChange,
    });
  });

  return (
    <Stack
      direction={direction}
      spacing={spacing}
      wrap={"wrap"}
      {...rootProps}
      {...rest}
    >
      {enhancedChildren}
    </Stack>
  );
};

/**
 *
 * @param {import("@chakra-ui/react").StackProps & {isSelected: boolean, value: object, onSelectChange: (value) => {}}} props
 * @returns
 */
const RadioCard = (props) => {
  const styles = useStyleConfig("Input", { variant: "outline" })?.field || {};
  const {
    isSelected,
    onSelectChange,
    value,
    onChange,
    children,
    allowChange,
    ...stackProps
  } = props;

  useEffect(() => {
    isSelected && handleClick();
  }, []);

  const handleClick = () => {
    if (allowChange) {
      onSelectChange?.(value);
      onChange?.(value); // optional per-card callback
    }
  };

  const getOpacity = () => {
    if (allowChange) return 1;
    else if (isSelected) return 1;
    else return 0.5;
  };

  return (
    <Stack
      __css={styles}
      h="max-content"
      minH="10px"
      userSelect="none"
      cursor={allowChange ? "pointer" : "not-allowed"}
      borderColor={isSelected ? "green.600" : styles?.borderColor}
      _hover={{
        borderColor: isSelected ? "green.600" : styles?._hover?.borderColor,
      }}
      onClick={handleClick}
      pos="relative"
      overflow={"hidden"}
      w="max-content"
      opacity={getOpacity()}
      {...stackProps}
    >
      {children}
      {isSelected && (
        <Box
          bg={"green.600"}
          h="14px"
          w="14px"
          pos="absolute"
          right={"-7px"}
          bottom={"-7px"}
          transform={"rotate(45deg)"}
        />
      )}
    </Stack>
  );
};

/**
 * Custom hook to show toasts with customized `success` and `error` components.
 *
 * @returns {(options: import("@chakra-ui/react").UseToastOptions) => void}
 */
export const useCustomToast = () => {
  const toast = useChakraToast();
  const toastIdRef = useRef();

  const getDescriptionParts = (text = "") => {
    const match = text.match(/\(([^)]+)\)/);
    return {
      description: text.replace(/\(([^)]+)\)/, "").trim(),
      more: match?.[1],
    };
  };

  /**
   * @param {{ title?: string, description?: string, status: "success" | "error" }} props
   * @returns {JSX.Element}
   */
  const CustomToast = ({ title, description, status }) => {
    const isSuccess = status === "success";
    const parsed = getDescriptionParts(description);
    const closeTimeoutRef = useRef(null);
    const [showMore, setShowMore] = useState(false);

    const handleHover = (isHovering) => {
      if (isHovering) {
        // Pause: clear any pending close
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        toast.update(toastIdRef.current, {
          duration: null,
          render: () => (
            <CustomToast
              title={title}
              description={description}
              status={status}
            />
          ),
        });
      } else {
        // Resume manually
        closeTimeoutRef.current = setTimeout(() => {
          toast.close(toastIdRef.current);
          closeTimeoutRef.current = null;
        }, 1500); // Resume after 3 seconds
      }
    };

    function closeToast() {
      toast.close(toastIdRef.current);
    }

    return (
      <Box
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
        bgGradient={
          isSuccess
            ? "linear(to-b, #002f17ed, #212123, #212123)"
            : "linear(to-b, #4b0601ed, #212123, #212123)"
        }
        color="white"
        px={4}
        py={3}
        borderRadius="lg"
        boxShadow={
          isSuccess
            ? "0px 10px 34px 0px rgba(67,247,40,0.10)"
            : "0px 10px 24px 0px #FE231520"
        }
        border={isSuccess ? "1px solid #1AB06250" : "1px solid #FE231530"}
        mb="4"
        maxW="350px"
        pos="relative"
      >
        <HStack spacing={3} align="flex-start">
          <Box
            h="22px"
            w="22px"
            boxShadow={
              isSuccess
                ? "0px 0px 0px 3px #1ab06230"
                : "0px 0px 0px 3px #FE231530"
            }
            borderRadius="full"
          >
            {isSuccess ? (
              <PiCheckCircleThin color="#1AB062" size={22} />
            ) : (
              <HiOutlineExclamationCircle color="#FE2315" size={22} />
            )}
          </Box>
          <VStack spacing={1} align="flex-start">
            <Text fontWeight="bold" fontSize="sm">
              {title || (isSuccess ? "Success" : "Error")}
            </Text>
            <Text fontSize="xs" color="gray.400">
              {parsed.description}
              {parsed.more && !showMore && (
                <Button
                  variant="link"
                  fontSize="xs"
                  ml="0.5px"
                  onClick={() => setShowMore(true)}
                >
                  See details
                </Button>
              )}{" "}
              {showMore && <>{parsed.more}</>}
            </Text>
          </VStack>
          <IconButton
            size="md"
            pos="absolute"
            right={"0"}
            top="0"
            variant={"glow"}
            icon={<HiXMark />}
            onClick={closeToast}
          />
        </HStack>
      </Box>
    );
  };

  /**
   * Triggers a custom toast.
   * @param {import("@chakra-ui/react").UseToastOptions & { status: "success" | "error" }} options
   */
  return (options) => {
    const { status, title, description, ...rest } = options;

    toastIdRef.current = toast({
      render: () => (
        <CustomToast title={title} description={description} status={status} />
      ),
      duration: 3500,
      status,
      ...rest,
    });
  };
};

/**
 *
 * @param {import("@chakra-ui/react").TooltipProps} props
 * @returns
 */
const _Tooltip = (props) => {
  return (
    <Tooltip
      placement="auto"
      hasArrow
      bg="white"
      color="black"
      borderRadius={"md"}
      fontSize={"12px"}
      {...props}
    >
      {props.children}
    </Tooltip>
  );
};

/**
 * @typedef {Object} CustomProps
 * @property {string} header - The header text for the InfoContainer.
 * @property {React.ReactNode} [children] - Optional content inside the InfoContainer.
 */

/**
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<CustomProps & import("@chakra-ui/react").ModalProps> & React.RefAttributes<any>>}
 */
const CustomModal = forwardRef(function InfoContainer(props, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const { children, header, ...rest } = props;

  // This exposes functions to the parent (hook)
  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  }));

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} {...rest}>
      <ModalOverlay />
      <ModalContent pb="3">
        <ModalHeader px="0" py="2.5">
          {header}
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <VStack overflow="scroll" w="full">
            <VStack fontSize="sm" align="flex-start" w="full">
              {children}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

export { Input };
export { Separator };
export { Skeleton };
export { _Select as Select };
export { _Image as Image };
export { _Tooltip as Tooltip };
export { TextEditable };
export { RadioCard };
export { RadioCardGroup };
export { CustomModal };
